import { useMemo } from "react";

import type { SxProps, Theme } from "@mui/material/styles";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";

import { neutral } from "app/themes/colors";
import { getScribeModalZIndex } from "features/medical-scribe/medical-scribe-constants";

type Viewport = {
  isMobile: boolean;
  isTablet: boolean;
  isCollapsed: boolean;
};

export const STACK_SX = {
  boxSizing: "border-box",
  height: "100%",
  gap: 2,
  width: "100%"
} as const;

export const VIEW_SCRIBE_DETAILS_SX = {
  flex: 1,
  minHeight: 0,
  width: "100%"
} as const;

/**
 * Beside the details, the list takes what is left after the details get `DETAILS_PANEL_PREFERRED`,
 * between `LIST_PANEL_MIN` and `LIST_PANEL_MAX`. At 1920px that is the full 633px; on a 1366–1536px
 * laptop the list gives up width so the details' buttons and tabs still fit. 520px still holds two
 * chips and Edit on one line.
 */
const LIST_PANEL_MAX = "633px";
const LIST_PANEL_MIN = "520px";
const DETAILS_PANEL_PREFERRED = "800px";
const LIST_PANEL_WIDTH = `clamp(${LIST_PANEL_MIN}, calc(100% - ${DETAILS_PANEL_PREFERRED}), ${LIST_PANEL_MAX})`;

// `isMobile` is `down("mobile")`, so it is also true on tablet widths and is checked first.
function getMinWidth({ isMobile, isTablet, isCollapsed }: Viewport) {
  if (isMobile) return 320;
  if (isTablet) return isCollapsed ? 600 : 1000;
  return isCollapsed ? 900 : 1538;
}

function getHeight({ isMobile, isTablet, isCollapsed }: Viewport) {
  if (isMobile) return isCollapsed ? "38vh" : "90vh";
  if (isTablet) return isCollapsed ? "35.5vh" : "85vh";
  return isCollapsed ? "480px" : "80vh";
}

function getDialogContentSx(viewport: Viewport, theme: Theme): SxProps<Theme> {
  return {
    display: "flex",
    p: viewport.isMobile ? 2 : 3,
    pb: 0,
    flexDirection: "column",
    boxSizing: "border-box",
    height: getHeight(viewport),
    minHeight: "665px",
    maxHeight: "calc(100dvh - 96px)",
    overflow: "hidden",
    border: `solid 1px ${neutral[500]}`,
    boxShadow: theme.shadows[3],
    background: neutral[50]
  };
}

function getListPanelMinWidth({ isMobile, isTablet }: Viewport) {
  if (isTablet) return "100%";
  if (isMobile) return LIST_PANEL_MAX;
  return LIST_PANEL_WIDTH;
}

function getLeftPanelSx(viewport: Viewport): SxProps<Theme> {
  const { isTablet } = viewport;
  return {
    height: isTablet ? "40vh" : "100%",
    overflowY: "auto",
    overflowX: "hidden",
    width: viewport.isMobile ? undefined : LIST_PANEL_WIDTH,
    minWidth: getListPanelMinWidth(viewport),
    maxWidth: isTablet ? "100%" : "none",
    borderRight: isTablet ? "none" : `solid 1px ${neutral[500]}`,
    borderBottom: isTablet ? `solid 1px ${neutral[500]}` : "none",
    flex: "0 0 auto",
    pb: isTablet ? 2 : 0
  };
}

function getRightPanelSx(
  isTablet: boolean,
  isEditScribePanel: boolean
): SxProps<Theme> {
  return {
    flex: isTablet ? "1 1 auto" : 1,
    minWidth: isTablet ? "100%" : "300px",
    minHeight: 0,
    height: "100%",
    overflowX: "hidden",
    mt: isTablet ? 0 : -1,
    display: "flex",
    flexDirection: "column",
    overflowY: isEditScribePanel ? "hidden" : "auto"
  };
}

/**
 * The dialog's size and the two panels' sizing at each breakpoint. Between 768px and 1320px the
 * list stacks above the details instead of beside them.
 */
export default function useInsertScribeLayout({
  isCollapsed,
  isEditScribePanel
}: {
  isCollapsed: boolean;
  isEditScribePanel: boolean;
}) {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.between("tablet", "mobile"));
  const isMobile = useMediaQuery(theme.breakpoints.down("mobile"));

  const sx = useMemo(() => {
    const viewport = { isMobile, isTablet, isCollapsed };
    return {
      dialogContent: getDialogContentSx(viewport, theme),
      dialogTitle: {
        border: `solid 1px ${neutral[400]}`,
        boxShadow: theme.shadows[2]
      },
      dialog: {
        zIndex: getScribeModalZIndex(theme.zIndex.snackbar),
        pointerEvents: isCollapsed ? "none" : "auto"
      } as SxProps<Theme>,
      leftPanel: getLeftPanelSx(viewport),
      rightPanel: getRightPanelSx(isTablet, isEditScribePanel)
    };
  }, [isMobile, isTablet, isCollapsed, isEditScribePanel, theme]);

  const minWidth = getMinWidth({ isMobile, isTablet, isCollapsed });

  // The shared Dialog turns `minWidth` into the paper's `min-width`, which beats its `max-width`,
  // so a 1538px dialog in a 1440px window was cut off on both sides. Cap it at the container minus
  // MUI's 32px paper margins. Its own memo: the Dialog rebuilds its Paper (remounting the content)
  // whenever this object changes, so it must change only with the width. Below the `mobile`
  // breakpoint the Dialog already sizes the paper to the window.
  const paperSx = useMemo(
    () =>
      isMobile
        ? undefined
        : {
            minWidth: `min(${theme.typography.pxToRem(minWidth)}, calc(100% - 64px))`
          },
    [isMobile, minWidth, theme]
  );

  return {
    isMobile,
    isTablet,
    minWidth,
    sx,
    paperSx
  };
}
